// src/services/authService.ts

import apiClient from '@/api/apiClient';
import userService from '@/services/userService';
import { RegisterFormData } from '@/stores/AuthStore';
import { AxiosResponse } from 'axios';

interface UserInfoOptions {
    fields?: string[];
    populate?: string[];
}

export interface LoginData {
    identifier: string;
    password: string;
}

export interface AuthResponse {
    jwt: string;
    user: {
        id: number;
        documentId: string;
        username: string;
        email: string;
        provider: string;
        confirmed: boolean;
        blocked: boolean;
        createdAt: string;
        updatedAt: string;
        publishedAt: string;
        locale: string | null;
        user_profile: any | null;
        groups: {
            id: number;
            documentId: string;
            name: string;
            code: string;
            description: string;
            createdAt: string;
            updatedAt: string;
            publishedAt: string;
            locale: string | null;
            registrationType: string;
            isActive: boolean;
            registrationMessage: string | null;
            order: number | null;
        }[];
        role: {
            id: number;
            documentId: string;
            name: string;
            description: string;
            type: string;
            createdAt: string;
            updatedAt: string;
            publishedAt: string;
            locale: string | null;
        };
        roleFunctionCodes?: string[];
        firstName?: string;
        lastName?: string;
    };
    needsApproval?: boolean;
}

export interface SendOtpPayload {
  email: string;
}

export interface VerifyOtpPayload {
  email: string;
  otp: string;
  newPassword: string;
}

export type AvailableValuePayload = {
    fieldName: 'username' | 'email' | 'phone';
    value: string;
}

/**
 * Service xử lý các thao tác xác thực
 */
class AuthService {
    /**
     * Đăng ký người dùng mới
     */
    async register(data: RegisterFormData): Promise<AuthResponse> {
        try {
            // Xử lý dữ liệu gửi đi để đảm bảo có groupCodes
            const registerData = {
                ...data,
                // Đảm bảo luôn có groupCodes, nếu không có thì dùng groupCode
                groupCodes: data.groupCodes && data.groupCodes.length > 0
                    ? data.groupCodes
                    : data.groupCode ? [data.groupCode] : []
            };

            const response = await apiClient.post('/api/custom-auth/register', registerData);

            // Nếu đăng ký thành công, lưu token
            if (response.data && response.data.jwt) {
                localStorage.setItem('token', response.data.jwt);
            }

            return response.data;
        } catch (error) {
            console.error('Registration error details:', error);
            throw error;
        }
    }

    /**
     * Đăng nhập
     */
    async login(data: LoginData): Promise<AuthResponse> {
        try {
            // 1. Đăng nhập bình thường
            const loginResponse = await apiClient.post('/api/auth/local', data);

            // Lưu token vào localStorage
            if (loginResponse.data && loginResponse.data.jwt) {
                localStorage.setItem('token', loginResponse.data.jwt);
            }

            // 2. Gọi API bổ sung để lấy thông tin đầy đủ
            const userDetails = await this.getUserWithProfileAndGroups();

            // 3. Kết hợp dữ liệu
            const enhancedResponse = {
                ...loginResponse.data,
                user: {
                    ...loginResponse.data.user,
                    user_profile: userDetails.user_profile,
                    groups: userDetails.groups,
                    role: userDetails.role,
                    roleFunctionCodes: userDetails.roleFunctionCodes || [],
                }
            };

            return enhancedResponse;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Kiểm tra username có tồn tại không
     */
    async checkUsername(username: string): Promise<{ available: boolean }> {
        try {
            const response = await apiClient.get(`/api/auth/check-username/${username}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Lấy thông tin người dùng hiện tại
     */
    async getCurrentUser(): Promise<AuthResponse['user']> {
        try {
            const response: AxiosResponse<AuthResponse['user']> = await apiClient.get('/api/users/me');
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Gửi yêu cầu đặt lại mật khẩu
     */
    async forgotPassword(email: string): Promise<{ ok: boolean }> {
        try {
            const response = await apiClient.post('/api/auth/forgot-password', { email });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Đặt lại mật khẩu
     */
    async resetPassword(code: string, password: string, passwordConfirmation: string): Promise<AuthResponse> {
        try {
            const response: AxiosResponse<AuthResponse> = await apiClient.post('/api/auth/reset-password', {
                code,
                password,
                passwordConfirmation,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Đăng xuất
     */
    logout(): void {
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('userRole');
    }

    /**
     * Kiểm tra trạng thái xác thực
     */
    isAuthenticated(): boolean {
        return !!localStorage.getItem('token');
    }
    isTokenValid() {
        try {
            const token = localStorage.getItem('token');
            if (!token) return false;

            // Phân tích token
            const parts = token.split('.');
            if (parts.length !== 3) return false;

            try {
                // Decode phần payload
                const payload = JSON.parse(atob(parts[1]));

                // Kiểm tra thời gian hết hạn
                const expiry = payload.exp * 1000; // Chuyển từ giây sang mili giây
                return Date.now() < expiry;
            } catch (err) {
                console.error('Error decoding token:', err);
                return false;
            }
        } catch (err) {
            console.error('Token validation error:', err);
            return false;
        }
    }

    /**
     * Lấy thông tin người dùng hiện tại với các tùy chọn
     */
    async getUserInfo(options: UserInfoOptions = {}): Promise<any> {
        try {
            // Xây dựng query parameters
            const queryParams = new URLSearchParams();

            if (options.fields && options.fields.length > 0) {
                queryParams.append('fields', options.fields.join(','));
            }

            if (options.populate && options.populate.length > 0) {
                options.populate.forEach((populate, index) => {
                    queryParams.append(`populate[${index}]`, populate);
                });
            }

            const queryString = queryParams.toString();
            const url = `/api/custom-auth/user-info${queryString ? `?${queryString}` : ''}`;

            const response: AxiosResponse = await apiClient.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching user info:', error);
            throw error;
        }
    }

    /**
     * Helper function để lấy thông tin người dùng với profile và nhóm
     */
    async getUserWithProfileAndGroups(): Promise<any> {
        // TODO REVIEW AGAIN
        try {
            const [userData, profile] = await Promise.allSettled([
                this.getUserInfo({
                    populate: ['user_profile', 'groups', 'role']
                }),
                userService.getCurrentUserProfile()
            ]);
            
            let userDataResult = null;
            let profileResult = null;

            if (userData.status === 'fulfilled') {
                userDataResult = userData.value;
            } else {
                console.error('Error fetching user data:', userData.reason);
            }

            if (profile.status === 'fulfilled') {
                profileResult = profile.value;
            } else {
                console.error('Error fetching user profile:', profile.reason);
            }

            const result = {
                ...userDataResult,
                firstName: profileResult?.firstName ?? null,
                lastName: profileResult?.lastName ?? null,
                fullName: profileResult?.fullName ?? null,
                avatar: profileResult?.avatar?.url ?? null,
                displayName: profileResult?.displayName ?? null,
                // user_profile: profileResult
            };

            return result;
        } catch (error) {
            console.error('Error fetching user with profile and groups:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách các role function codes của user
     */
    async getRoleFunctionCodes(): Promise<string[]> {
        try {
            const response = await apiClient.get('/api/custom-auth/role-functions');
            return response.data || [];
        } catch (error) {
            console.error('Error fetching role function codes:', error);
            return [];
        }
    }
    
    // Gửi OTP tới email
    async sendOtp(payload: SendOtpPayload): Promise<string> {
        try {
            const response = await apiClient.post('/api/custom-auth/send-otp', payload, {
                headers: { "Content-Type": "application/json" },
            });
            return response.data;
        } catch (error: any) {
            throw error || { message: "Failed to send OTP" };
        }
    }

    // Xác thực OTP và reset password
    async verifyOtp(payload: VerifyOtpPayload): Promise<any> {
        try {
            const response = await apiClient.post('/api/custom-auth/verify-otp', payload, {
                headers: { "Content-Type": "application/json" },
            });
            return response.data;
        } catch (error: any) {
            throw error || { message: "Failed to verify OTP" };
        }
    };

    // Optional: dùng forgotPassword của users-permissions plugin
    async forgotPasswordLink(email: string):Promise<any> {
        try {
            const response = await apiClient.post('/api/auth/forgot-password', { email }, {
                headers: { "Content-Type": "application/json" },
            });
            return response.data;
        } catch (error: any) {
            throw error.response?.data || { message: "Failed to request password reset" };
        }
    };

    // Check value available
    async checkAvailableValue(payload: AvailableValuePayload): Promise<any> {
        try {
            const response = await apiClient.post('/api/custom-auth/available-value', payload, {
                headers: { "Content-Type": "application/json" },
            });
            return response.data;
        } catch (error: any) {
            console.error(`❌ Check Available Value Failed: ${error?.message}!`);
            throw error.response?.data?.error;
        }
    };
}

export default new AuthService();
